import { useState } from 'react';
import { X } from 'lucide-react';
import { Card } from '../ui/Card';
import axios from 'axios';
import toast from 'react-hot-toast';

interface LogMealModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const MEAL_TYPES = [
  { value: 'BREAKFAST', label: 'Mic Dejun', emoji: '🌅' },
  { value: 'LUNCH', label: 'Prânz', emoji: '☀️' },
  { value: 'DINNER', label: 'Cină', emoji: '🌙' },
  { value: 'SNACK', label: 'Snack', emoji: '🍎' }
];

// Returns local datetime string in format "YYYY-MM-DDTHH:mm" (for datetime-local input)
function getLocalDateTimeString(date = new Date()) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function LogMealModal({ isOpen, onClose, onSuccess }: LogMealModalProps) {
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    mealType: 'LUNCH',
    consumedAt: getLocalDateTimeString(),
    foods: '',
    notes: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.foods.trim()) {
      toast.error('Adaugă cel puțin un aliment');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('accessToken');
      
      // Split foods by comma and clean
      const foodsArray = formData.foods
        .split(',')
        .map(f => f.trim())
        .filter(f => f.length > 0);

      if (foodsArray.length === 0) {
        toast.error('Adaugă cel puțin un aliment');
        return;
      }

      // Convert datetime-local to ISO 8601
      const isoDateTime = new Date(formData.consumedAt).toISOString();

      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/tracking/meals`,
        {
          ...formData,
          consumedAt: isoDateTime,
          foods: foodsArray
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Masă înregistrată cu succes');
      onSuccess();
      onClose();
      
      // Reset form
      setFormData({
        mealType: 'LUNCH',
        consumedAt: getLocalDateTimeString(),
        foods: '',
        notes: ''
      });
    } catch (error: any) {
      console.error('Error logging meal:', error);
      toast.error(error.response?.data?.error || 'Eroare la înregistrare');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold flex items-center gap-2">
              🍽️ Log Masă
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Meal Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Tip Masă *
              </label>
              <div className="grid grid-cols-2 gap-3">
                {MEAL_TYPES.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, mealType: type.value })}
                    className={`p-3 border-2 rounded-lg transition ${
                      formData.mealType === type.value
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-orange-300'
                    }`}
                  >
                    <div className="text-2xl mb-1">{type.emoji}</div>
                    <div className="text-sm font-medium">{type.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Date/Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data și ora
              </label>
              <input
                type="datetime-local"
                value={formData.consumedAt}
                onChange={(e) => setFormData({ ...formData, consumedAt: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                required
              />
            </div>

            {/* Foods */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Alimente *
              </label>
              <textarea
                value={formData.foods}
                onChange={(e) => setFormData({ ...formData, foods: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                placeholder="Introdu alimentele separate prin virgulă:&#10;Pâine integrală, brânză telemea, roșii, castravete, măsline"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Separă alimentele cu virgulă
              </p>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note (opțional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                placeholder="Porție mare, mâncare gătită acasă..."
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Anulează
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Se salvează...' : 'Salvează'}
              </button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
}
